import { GridLocaleText } from "@mui/x-data-grid";
import { useTranslations } from "@fenetech/translations";

export const useDataGridLocale = (): GridLocaleText => {

    const tm = useTranslations();

    //All fields are defined for DataGridLocalText props, however only those used in Web Sauce have translations to avoid unnecessary entries in TM.
    return {
        // Root
        noRowsLabel: tm.Get("No data available"),
        noResultsOverlayLabel: tm.Get('No results were found.'),
        errorOverlayDefaultLabel: tm.Get('An unknown error has occurred.'),

        // Density selector toolbar button text
        toolbarDensity: tm.Get('Density'),
        toolbarDensityLabel: tm.Get('Density'),
        toolbarDensityCompact: tm.Get('Compact'),
        toolbarDensityStandard: tm.Get('Standard'),
        toolbarDensityComfortable: tm.Get('Comfortable'),

        // Columns selector toolbar button text
        toolbarColumns: tm.Get('Columns'),
        toolbarColumnsLabel: tm.Get('Select columns'),

        // Filters toolbar button text
        toolbarFilters: tm.Get('Filters'),
        toolbarFiltersLabel: tm.Get('Show filters'),
        toolbarFiltersTooltipHide: tm.Get('Hide filters'),
        toolbarFiltersTooltipShow: tm.Get('Show filters'),
        toolbarFiltersTooltipActive: (count) => count !== 1 ? tm.GetWithParams('{0} active filters', count.toString()) : tm.Get(`1 active filter`),

        // Quick filter toolbar field
        toolbarQuickFilterPlaceholder: 'Search…',
        toolbarQuickFilterLabel: 'Search',
        toolbarQuickFilterDeleteIconLabel: 'Clear',

        // Export selector toolbar button text
        toolbarExport: tm.Get('Download'),
        toolbarExportLabel: tm.Get('Download'),
        toolbarExportCSV: tm.Get('CSV'),
        toolbarExportPrint: tm.Get('Print'),
        toolbarExportExcel: 'Download as Excel',

        // Columns panel text
        columnsPanelTextFieldLabel: tm.Get('Find column'),
        columnsPanelTextFieldPlaceholder: tm.Get('Column title'),
        columnsPanelDragIconLabel: tm.Get('Reorder column'),
        columnsPanelShowAllButton: tm.Get('Show all'),
        columnsPanelHideAllButton: tm.Get('Hide all'),

        // Filter panel text
        filterPanelAddFilter: tm.GetWithParams('Add {0}', "Filter"),
        filterPanelDeleteIconLabel: tm.Get('Delete'),
        filterPanelLinkOperator: 'Logic operator',
        filterPanelOperators: tm.Get('Operator'),
        filterPanelOperatorAnd: tm.Get('And'),
        filterPanelOperatorOr: tm.Get('Or'),
        filterPanelColumns: tm.Get('Column'),
        filterPanelInputLabel: tm.Get('Value'),
        filterPanelInputPlaceholder: tm.Get('Value'),

        // Filter operators text
        filterOperatorContains: tm.Get("Contains"),
        filterOperatorEquals: tm.Get("Equals"),
        filterOperatorStartsWith: tm.Get("Starts with"),
        filterOperatorEndsWith: tm.Get("Ends with"),
        filterOperatorIs: 'is',
        filterOperatorNot: 'is not',
        filterOperatorAfter: 'is after',
        filterOperatorOnOrAfter: 'is on or after',
        filterOperatorBefore: 'is before',
        filterOperatorOnOrBefore: 'is on or before',
        filterOperatorIsEmpty: tm.Get('Is Empty'),
        filterOperatorIsNotEmpty: tm.Get('Is Not Empty'),
        filterOperatorIsAnyOf: tm.Get('Is Any Of'),

        // Filter values text
        filterValueAny: 'any',
        filterValueTrue: 'true',
        filterValueFalse: 'false',

        // Column menu text
        columnMenuLabel: tm.Get('Menu'),
        columnMenuShowColumns: tm.Get('Show columns'),
        columnMenuFilter: tm.Get('Filter'),
        columnMenuHideColumn: tm.Get('Hide'),
        columnMenuUnsort: tm.Get('Remove Sort'),
        columnMenuSortAsc: tm.Get('Sort Ascending'),
        columnMenuSortDesc: tm.Get('Sort Descending'),

        // Column header text
        columnHeaderFiltersTooltipActive: (count) => tm.Get("Filters"),
        columnHeaderFiltersLabel: tm.Get('Show filters'),
        columnHeaderSortIconLabel: tm.Get('Sort'),

        // Rows selected footer text
        footerRowSelected: (count) => tm.GetWithParams("{0} row(s) selected.", count.toString()),

        // Total row amount footer text
        footerTotalRows: 'Total Rows:',

        // Total visible row amount footer text
        footerTotalVisibleRows: (visibleCount, totalCount) => `${visibleCount.toLocaleString()} of ${totalCount.toLocaleString()}`,

        // Checkbox selection text
        checkboxSelectionHeaderName: tm.Get('Checkbox selection'),
        checkboxSelectionSelectAllRows: 'Select all rows',
        checkboxSelectionUnselectAllRows: 'Unselect all rows',
        checkboxSelectionSelectRow: 'Select row',
        checkboxSelectionUnselectRow: 'Unselect row',

        // Boolean cell text
        booleanCellTrueLabel: 'yes',
        booleanCellFalseLabel: 'no',

        // Actions cell more text
        actionsCellMore: 'more',

        // Column pinning text
        pinToLeft: tm.Get('Pin to left'),
        pinToRight: tm.Get('Pin to right'),
        unpin: tm.Get('Unpin'),

        // Tree Data
        treeDataGroupingHeaderName: 'Group',
        treeDataExpand: 'see children',
        treeDataCollapse: 'hide children',

        // Grouping columns
        groupingColumnHeaderName: 'Group',
        groupColumn: (name) => `Group by ${name}`,
        unGroupColumn: (name) => `Stop grouping by ${name}`,

        // Master/detail
        detailPanelToggle: 'Detail panel toggle',
        expandDetailPanel: 'Expand',
        collapseDetailPanel: 'Collapse',

        // Used core components translation keys
        MuiTablePagination: {
            labelRowsPerPage: tm.Get("Rows Per Page"),
            labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} ${tm.Get("of")} ${count}`
        },

        // Row reordering text
        rowReorderingHeaderName: 'Row reordering',

        // Aggregation
        aggregationMenuItemHeader: 'Aggregation',
        aggregationFunctionLabelSum: 'sum',
        aggregationFunctionLabelAvg: 'avg',
        aggregationFunctionLabelMin: 'min',
        aggregationFunctionLabelMax: 'max',
        aggregationFunctionLabelSize: 'size',
    }
}